import { Link } from "react-router-dom";
import type { NewsArticle } from "@/types/news";
import { formatHeat } from "@/utils/news";

interface HeroSectionProps {
  lead: NewsArticle;
  hotList: NewsArticle[];
}

function HeroSection({ lead, hotList }: HeroSectionProps) {
  return (
    <section className="relative w-full">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20amber%20and%20deep%20red%20stage%20spotlights%20with%20soft%20light%20beams%20and%20bokeh%20particles%20over%20dark%20background%2C%20dramatic%20theatrical%20atmosphere%2C%20no%20people%2C%20cinematic%20editorial%20photography%20high%20detail&width=1600&height=900&seq=guaspeed-hero-main&orientation=landscape"
          alt="瓜圈速报突发爆料现场氛围图"
          title="瓜圈速报 - 突发爆料第一手快讯"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/70 to-foreground-950/85" />
      </div>

      <div className="relative w-full px-4 md:px-10 pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/90 text-background-50 dark:text-foreground-950 text-xs font-medium">
              <span className="w-2 h-2 rounded-full bg-background-50 animate-live" />
              刚刚 · 突发爆料持续更新
            </span>

            <h1 className="mt-6 font-heading text-4xl md:text-6xl font-black text-background-50 dark:text-foreground-950 leading-tight tracking-wide">
              瓜圈速报
            </h1>

            <p className="mt-5 text-base md:text-lg text-background-200/90 dark:text-foreground-800/90 leading-relaxed max-w-2xl">
              在吃瓜圈里最怕的就是错过第一口，新瓜刚出现时根本来不及找。我们把突发、最新进展、快讯第一时间同步出来，后续回应和相关动态也会跟住，打开就能抢在别人讨论之前看懂来龙去脉。
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/article/guaspeed-2026-0910-a1"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-flashlight-fill" />
                </span>
                看最新速报
              </Link>
              <a
                href="#live-feed"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 dark:bg-foreground-950/15 text-background-50 dark:text-foreground-950 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-background-50/25 dark:hover:bg-foreground-950/25 transition-colors border border-background-50/25 dark:border-foreground-950/25"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                快讯时间流
              </a>
            </div>

            <dl className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-xl">
              <div>
                <dt className="text-xs text-background-300/80 dark:text-foreground-700/80">今日速报</dt>
                <dd className="mt-1 font-heading text-2xl font-black text-accent-400">12 条</dd>
              </div>
              <div>
                <dt className="text-xs text-background-300/80 dark:text-foreground-700/80">突发追踪中</dt>
                <dd className="mt-1 font-heading text-2xl font-black text-accent-400">3 条</dd>
              </div>
              <div>
                <dt className="text-xs text-background-300/80 dark:text-foreground-700/80">平均更新</dt>
                <dd className="mt-1 font-heading text-2xl font-black text-accent-400">8 分钟</dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-background-50/95 backdrop-blur rounded-lg border border-background-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 bg-background-100">
                <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-fire-fill" />
                  </span>
                  热瓜榜 · 实况
                </h2>
                <Link
                  to="/category/all"
                  className="text-xs text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  全部速报
                </Link>
              </div>

              <ol className="divide-y divide-background-200">
                {hotList.map((item, idx) => (
                  <li key={item.id}>
                    <Link
                      to={`/article/${item.id}`}
                      className="flex items-start gap-4 px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span
                        className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-md font-heading text-sm font-black ${
                          idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-foreground-900 leading-snug line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                          <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900">
                            {item.categoryName}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3.5 h-3.5 flex items-center justify-center text-primary-500">
                              <i className="ri-fire-line" />
                            </span>
                            {formatHeat(item.heat)}
                          </span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>

              <div className="px-5 py-4 bg-background-100 border-t border-background-200">
                <Link
                  to={`/article/${lead.id}`}
                  className="flex items-center gap-3 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-circle-line" />
                  </span>
                  <span className="line-clamp-1">{lead.title}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;